import React, { useEffect, useState } from "react";
import { Popup } from "react-map-gl";
import { Box, IconButton, Link, Tooltip, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import openRoads from "../../static/openroads.webp";
import { PRETTY_BLUE } from "../trips/form/BorderWithLabel";
import { OPEN_ROADS_END_MESSAGE, OPEN_ROADS_LINK, OPEN_ROADS_START_MESSAGE, } from "../UserVisibleStrings";
import { ZIndexLevels } from "./geoUtils";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";

type Props = {
    latitude: number,
    longitude: number,
    text: string,
    description: string,
    imageUrl: string,
    copyAddress: string,
    onMouseLeave: () => void,
    hoverTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
};

const OpenRoadsPopup: React.FC<Props> = ( {
    latitude,
    longitude,
    text,
    description,
    imageUrl,
    copyAddress,
    onMouseLeave,
    hoverTimeoutRef,
} ) =>
{
    const [ imageError, setImageError ] = useState( false );

    useEffect( () =>
    {
        setImageError( false );
    }, [ imageUrl ] );

    const handleCopyAddress = async () =>
    {
        try
        {
            await navigator.clipboard.writeText( copyAddress );
        }
        catch ( err )
        {
            console.error( "Failed to copy address: ", err );
        }
    };


    return (
        <Popup
            latitude = {latitude}
            longitude = {longitude}
            closeButton = {false}
            closeOnClick = {false}
            style = {{ paddingBottom: "6px", zIndex: ZIndexLevels.UIElement }}
            className = "custom-Thumbnail-Marker-Popup"
        >
            <Box
                sx = {{
                    padding: "10px",
                    width: "240px",
                    backgroundColor: "white",
                    border: `2px solid ${PRETTY_BLUE}`,
                    borderRadius: "8px",
                }}
                onMouseEnter = {() =>
                {
                    if ( hoverTimeoutRef.current )
                    {
                        clearTimeout( hoverTimeoutRef.current );
                    }
                }}
                onMouseLeave = {onMouseLeave}
            >
                {/* Header Row */}
                <Box
                    sx = {{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                    }}
                >
                    <Box
                        sx = {{
                            width: "36px",
                            height: "36px",
                            borderRadius: "50%",
                            border: `2px solid ${PRETTY_BLUE}`,
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f0f0f0",
                            flexShrink: 0,
                        }}
                    >
                        {imageError ? (
                            <LocalGasStationIcon sx = {{ color: PRETTY_BLUE, fontSize: 24 }}/>
                        ) : (
                            <Box
                                component = "img"
                                src = {imageUrl}
                                alt = {text}
                                onError = {() => setImageError( true )}
                                sx = {{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block"
                                }}
                            />
                        )}
                    </Box>

                    <Typography
                        sx = {{
                            color: "#000",
                            fontWeight: "bold",
                            fontSize: "14px",
                            textAlign: "center",
                            flexGrow: 1,
                            mx: 1,
                        }}
                    >
                        {text}
                    </Typography>

                    <Box
                        component = "img"
                        src = {openRoads}
                        alt = "Open Roads Logo"
                        sx = {{
                            width: "36px",
                            height: "36px",
                            objectFit: "contain",
                        }}
                    />
                </Box>

                {/* Description */}
                <Typography
                    sx = {{
                        color: "#333",
                        fontWeight: "normal",
                        fontSize: "12px",
                        mb: 1,
                        textAlign: "center",
                    }}
                >
                    {description}
                    <Tooltip title = "Copy address">
                        <IconButton
                            size = "small"
                            onClick = {handleCopyAddress}
                            sx = {{ padding: "4px" }}
                        >
                            <ContentCopyIcon fontSize = "small" sx = {{ color: PRETTY_BLUE }}/>
                        </IconButton>
                    </Tooltip>
                </Typography>

                {/* Link Section */}
                <Typography
                    sx = {{
                        color: "#333",
                        fontWeight: "normal",
                        fontSize: "11px",
                        textAlign: "center",
                    }}
                >
                    {OPEN_ROADS_START_MESSAGE}
                    <Link
                        href = "http://apply.myopenroads.com/r/rvweather"
                        target = "_blank"
                        rel = "noopener noreferrer"
                        sx = {{
                            color: PRETTY_BLUE,
                            textDecoration: "underline",
                            mx: 0.5,
                        }}
                    >
                        {OPEN_ROADS_LINK}
                    </Link>
                    {OPEN_ROADS_END_MESSAGE}
                </Typography>
            </Box>
        </Popup>
    );
};

export default OpenRoadsPopup;
